import React, { useContext, useState } from "react";
import { Card } from "tea-component";
import './index.scss';
import SvgIcon from "@home/components/SvgIcon";
import { getLanguage, getName } from "@home/utils/common";
import { useLocal } from "@/pages/dashboard/utils/customHook";
import { dynamicString } from "@home/utils/dynamicString";
import classNames from "classnames";
import { getImg } from "@home/_config/getImgs";
import { getExplainVal } from "@/pages/dashboard/utils/getExplainVal";
import { useParams, useSelector } from "umi";
import { LayoutContext } from "@/pages/benchmark/state/LayoutContext";
import CommonBubble from "@home/components/CommonBubble";
import DbbSegment from "@/pages/benchmark/components/DbbSegment";
import FormatHtmlTextToReact from "@home/components/FormatHtmlTextToReact";
import MetricDetailLineChart from "../MetricDetailLineChart";
import { handleDownload } from "@/pages/dashboard/utils/utils";
import dataFormat from "@home/utils/dbDataFormat/dataFormat";
import { getMetricFormatRule, getValueFromData } from "@/pages/benchmark/utils/utils";
import { DEFAULT_CHART_SERIES_CONFIG, ChartSeriesConfig } from "@/pages/benchmark/_config/constant";

interface ContentsTitles {
  titleEn: string;
  titleCn: string;
  className?: string;
  style?: React.CSSProperties;
  rainbowKey: string;
  isHaveValueExplain: boolean;
  titleTipPlacement: string;
  showExplainAtBottom: boolean; // 是否把指标解释展示在下方
  titleTipEn: string;
  titleTipCn: string;
}
type DataSourceItem = {
  source: string;  // 数据源标识
  source_en: string;  // 英文名称
  source_cn: string;  // 中文名称
  icon: string;
};

interface MetricDetailCard {
  titleEn: string;
  titleCn: string;
  className?: string;
  style?: React.CSSProperties;
  subTitleKey?: { [key: string]: string | number; }; // 副标题动态字符串内容
  rightTextKey?: { [key: string]: string | number; }; // 左侧文案动态字符串内容
  contents: Array<string | number> | string | number; // 展示数据内容
  tooltipEn: string;
  tooltipCn: string;
  valueTipEn: string;
  valueTipCn: string;
  contentsTitles: ContentsTitles[]; // 内容标题
  dataSources?: DataSourceItem[]; // 数据源
  explainInfo: any;
  groupNameEn: string; // 分组名称
  groupNameCn: string; // 分组名称
  config: {
    subTitleEn: string;
    subTitleCn: string;
    rightTextEn: string; // 左侧文案
    rightTextCn: string;
    height?: number;
    sourceTitleEn: string; // 数据源文案
    sourceTitleCn: string;
  };
  metricGroupList: any[];
  metricData: any;
  chartConfig?: {
    seriesConfig?: ChartSeriesConfig[];
  };
}

export default function MetricDetailCard(props: MetricDetailCard) {
  const {
    style,
    className,
    titleEn,
    titleCn,
    config,
    subTitleKey = {},
    rightTextKey = {},
    contents = [],
    tooltipEn,
    tooltipCn,
    valueTipEn,
    valueTipCn,
    contentsTitles,
    dataSources = [],
    explainInfo,
    metricGroupList,
    metricData,
    chartConfig
  } = props;
  const {
    subTitleEn,
    subTitleCn,
    rightTextEn,
    rightTextCn,
    sourceTitleEn,
    sourceTitleCn
  } = config || {};

  const [isHover, setIsHover] = useState(false);
  const [activeTab, setActiveTab] = useState('default');
  const [isValueTipHover, setIsValueTipHover] = useState(false);
  const { isEN, locale } = useLocal();
  const subTitle = getLanguage(isEN, subTitleEn, subTitleCn);
  const rightText = getLanguage(isEN, rightTextEn, rightTextCn);
  const explain = getLanguage(isEN, tooltipEn, tooltipCn);
  const valueExplain = getLanguage(isEN, valueTipEn, valueTipCn);
  const title = getLanguage(isEN, titleEn, titleCn);
  const { frameType } = useContext(LayoutContext);
  const { explainInfo: rainbowExplainInfo } = useSelector((state: any) => ({
    explainInfo: state?.benchmark?.allExplainInfo?.[frameType || 'pcconsole']
  }));
  const { metric } = useParams<{ metric: string; }>();

  // 标题浮窗
  const portItem = <div className="port-item">
    <div
      onMouseOver={() => setIsHover(true)}
      onMouseOut={() => setIsHover(false)}
    >
      <CommonBubble
        dark
        arrowPointAtCenter
        placement="top-start"
        content={explain ? <FormatHtmlTextToReact text={explain} /> : null}
      >
        <div>
          <SvgIcon
            className="port-icon"
            type={isHover ? 'alert-circle-action' : 'alert-circle-m'}
          />
        </div>
      </CommonBubble>
    </div>
  </div>;

  // 标题内容
  let titleContent;
  if (metricGroupList?.length > 1) {
    const { group_name_en, group_name_cn } = explainInfo?.[metric] || {};
    titleContent = <DbbSegment
      value={activeTab}
      options={[
        {
          text: { textEn: titleEn, textCn: titleCn },
          value: 'default',
          info: { textEn: tooltipEn, textCn: tooltipCn }
        },
        {
          text: { textEn: group_name_en, textCn: group_name_cn },
          value: 'group'
        }
      ]}
      onChange={setActiveTab}
    />;
  }
  else {
    titleContent = (
      <>
        {title}
        {portItem}
      </>);
  }

  const download = () => {
    const { group_name_en, group_name_cn } = explainInfo?.[metric] || {};
    const downloadTile = getName(isEN, group_name_en, group_name_cn);

    const tableHeaders = [
      {
        key: 'name',
        header: downloadTile,
      },
    ];

    // 动态生成指标列
    const metricColList =
      metricGroupList?.map((item: any) => {
        const { group_label_en, group_label_cn, metric: colMetric } = item || {};
        const name = getName(isEN, group_label_en, group_label_cn);
        return {
          key: colMetric,
          header: name,
        };
      }) || [];

    tableHeaders.push(...metricColList);

    // 根据 chartConfig.seriesConfig 动态生成数据行，如果没有配置则使用默认配置
    const seriesConfig = chartConfig?.seriesConfig || DEFAULT_CHART_SERIES_CONFIG;
    
    // 过滤出有数据的 series 配置
    const activeSeriesConfig = seriesConfig.filter((config: ChartSeriesConfig) => {
      const hasData = metricGroupList?.some((item: any) => {
        const value = getValueFromData(metricData?.data?.[item?.metric], config.dataKey);
        return value !== undefined && value !== null && value !== '';
      });
      return hasData;
    });

    const tableData = activeSeriesConfig.map((config: ChartSeriesConfig & { skipFormat?: boolean }) => {
      const { nameEn, nameCn, dataKey, skipFormat } = config;
      const dataItem: any = {
        name: getName(isEN, nameEn, nameCn),
      };
      metricGroupList?.forEach((item: any) => {
        const { metric: colMetric } = item || {};
        const value = getValueFromData(metricData?.data?.[colMetric], dataKey);
        
        // 如果配置了 skipFormat，直接返回原始值（字符串形式，防止 Excel 自动格式化）
        if (skipFormat) {
          // 确保返回字符串类型，避免 Excel 自动处理数字
          dataItem[colMetric] = value !== undefined && value !== null ? String(value) : '';
        } else {
          dataItem[colMetric] = dataFormat({ value, formatRule: getMetricFormatRule(item) });
        }
      });
      return dataItem;
    });
    
    const tableConfig = {
      option: tableHeaders,
      downloadData: tableData
    };
    handleDownload(tableConfig, downloadTile);
  };


  return (
    <Card className={classNames('dbb-metric-card', className)} style={style}>
      <Card.Body
        title={
          <div className="title-with-icon">
            {titleContent}
            <div className="right-text">
              <div className="language-group">
                {getLanguage(isEN, sourceTitleEn, sourceTitleCn)}
              </div>
              <div className="icon-text-container">
                {dataSources?.some(item => Object.values(item)?.some(Boolean)) ? (
                  dataSources?.map((item, index) => {
                    const { source, source_en: nameEn, source_cn: nameCn, icon } = item || {};

                    return (
                      <CommonBubble
                        key={index}
                        style={{ maxWidth: '370px' }}
                        content={source ? getExplainVal(rainbowExplainInfo, nameEn, locale) : ''}
                        placement="top"
                      >
                        <div className="icon-text-group">
                          <img
                            src={icon || getImg(source)}
                            className={source === "otdr" ? "icon-otdr" : "icon"}
                            alt={source}
                          />
                          {nameEn ? getLanguage(isEN, nameEn, nameCn) : source}
                        </div>
                      </CommonBubble>
                    );
                  })
                ) : (
                  '-'
                )}
              </div>
              {
                activeTab === 'default' ? (
                  <div className="dynamic-text-group">
                    {dynamicString(rightText, rightTextKey)}
                  </div>
                ) : <i onClick={download}><SvgIcon className={'metric-down-icon'} type="download" /></i>
              }
            </div>
          </div>
        }
        subtitle={
          activeTab === 'default' && (
            <span className="sub-title-text">
              {dynamicString(subTitle, subTitleKey)}
            </span>
          )
        }
      >
        {
          activeTab === 'default' ? (
            <div className="flex-container">
              {contentsTitles.map((item, index) => (
                <div key={index} className="flex-item">
                  <div className="item-content">
                    <CommonBubble
                      style={{ maxWidth: '350px' }}
                      content={getExplainVal(explainInfo, item?.rainbowKey, locale)}
                      placement="top"
                    >
                      <div
                        className={item?.className}
                        style={{ cursor: item?.titleTipEn || item?.titleTipCn ? 'pointer' : 'default' }}
                      >
                        {getLanguage(isEN, item?.titleEn, item?.titleCn)}
                      </div>
                    </CommonBubble>
                    <div className="value-with-icon">
                      <div style={{ fontWeight: 'bold', fontSize: '24px' }}>
                        {contents[index] || "-"}
                      </div>
                      {item?.isHaveValueExplain && !item?.showExplainAtBottom &&
                        <div className="port-item">
                          <div
                            onMouseOver={() => setIsValueTipHover(true)}
                            onMouseOut={() => setIsValueTipHover(false)}
                          >
                            <CommonBubble
                              dark
                              arrowPointAtCenter
                              placement={item?.titleTipPlacement ? item.titleTipPlacement : undefined}
                              style={{ maxWidth: '800px' }}
                              content={valueExplain ? <FormatHtmlTextToReact text={valueExplain} /> : null}
                            >
                              <div>
                                <SvgIcon
                                  className="port-icon"
                                  type={isValueTipHover ? 'alert-circle-action' : 'alert-circle-m'}
                                />
                              </div>
                            </CommonBubble>
                          </div>
                        </div>
                      }
                    </div>
                    {valueExplain && item?.showExplainAtBottom && (
                      <div className="margin-t-20">
                        <strong>{getLanguage(isEN, 'Description', '说明')}:</strong><br />
                        <FormatHtmlTextToReact text={valueExplain} />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : <MetricDetailLineChart metricGroupList={metricGroupList} metricData={metricData} metricConfig={explainInfo} chartConfig={chartConfig} />
        }
      </Card.Body>
    </Card>
  );
}
